import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Package, Search, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { PDPricingSettings } from "@/components/PDPricingSettings";

const STATUSES = [
  { key: "booked", label: "Booking Confirmed" },
  { key: "picked_up", label: "Picked Up" },
  { key: "in_transit", label: "In Transit" },
  { key: "out_for_delivery", label: "Out for Delivery" },
  { key: "delivered", label: "Delivered" },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

export default function PickupDropBookingsPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/pickup-drop/bookings").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, shipment_status) => {
    const prev = rows;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, shipment_status } : r)));
    try {
      await api.patch(`/pickup-drop/bookings/${id}`, { shipment_status });
      toast.success(`Status → ${STATUS_LABEL[shipment_status]}`);
    } catch (e) {
      setRows(prev);
      toast.error(apiErr(e));
    }
  };

  const itemsLabel = (r) => {
    const cats = (r.parcel?.subcategories) || [];
    const manual = (r.parcel?.manual_items) || [];
    return [...cats, ...manual.map((m) => `${m.name}×${m.quantity}`)].join(", ") || "—";
  };

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [r.booking_id, r.customer_name, r.customer_phone, r.pickup, r.delivery].some((v) => String(v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6" data-testid="pickup-drop-bookings-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight flex items-center gap-2"><Package className="h-7 w-7 text-primary" /> Pickup & Drop</h1>
          <p className="text-muted-foreground mt-1 text-sm">Confirmed parcel bookings. Update the status to action each order.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="rounded-none pl-9" placeholder="Search ID, name, city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pd-bookings-search" />
        </div>
      </div>

      <PDPricingSettings />

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground">No pickup & drop bookings yet.</div>
      ) : (
        <>
          <div className="hidden lg:block border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2.5">Booking</th>
                  <th className="text-left px-3 py-2.5">Customer</th>
                  <th className="text-left px-3 py-2.5">Route</th>
                  <th className="text-left px-3 py-2.5">Items</th>
                  <th className="text-left px-3 py-2.5">Cost</th>
                  <th className="text-left px-3 py-2.5">ETA</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/40" data-testid={`pd-booking-row-${r.booking_id}`}>
                    <td className="px-3 py-2.5"><span className="font-mono-num font-bold text-primary">{r.booking_id}</span><div className="text-[11px] text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div></td>
                    <td className="px-3 py-2.5">{r.customer_name}<div className="text-xs text-muted-foreground">{r.customer_phone}</div></td>
                    <td className="px-3 py-2.5 text-xs max-w-[240px]"><div className="truncate">{r.pickup}</div><div className="truncate text-muted-foreground">→ {r.delivery}</div></td>
                    <td className="px-3 py-2.5 text-xs max-w-[200px] truncate">{itemsLabel(r)}</td>
                    <td className="px-3 py-2.5 font-mono-num">₹{Number(r.cost || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2.5 text-xs">{r.estimated_delivery_date || "—"}</td>
                    <td className="px-3 py-2.5">
                      <select value={r.shipment_status} onChange={(e) => updateStatus(r.id, e.target.value)} data-testid={`pd-status-select-${r.booking_id}`}
                        className="h-9 rounded-none border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                        {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden space-y-3" data-testid="pd-bookings-cards">
            {filtered.map((r) => (
              <div key={r.id} className="border border-border bg-card p-4" data-testid={`pd-booking-card-${r.booking_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-num font-bold text-primary">{r.booking_id}</span>
                  <span className="text-[11px] text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                </div>
                <div className="text-sm mt-1">{r.customer_name}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-1">
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.customer_phone}</span>
                  {r.customer_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{r.customer_email}</span>}
                </div>
                <div className="text-xs mt-2">{r.pickup} <span className="text-muted-foreground">→</span> {r.delivery}</div>
                <div className="text-xs text-muted-foreground mt-1">{itemsLabel(r)}</div>
                <div className="flex items-center justify-between gap-2 mt-3">
                  <span className="font-mono-num font-bold">₹{Number(r.cost || 0).toLocaleString("en-IN")}</span>
                  <select value={r.shipment_status} onChange={(e) => updateStatus(r.id, e.target.value)} data-testid={`pd-status-select-m-${r.booking_id}`}
                    className="h-9 rounded-none border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                    {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
